import ClinicList from "@components/Locations/ClinicList2";
import Insurances, {getCareInsuranceText} from "@components/Reason/Insurances";
import {RelatedLinks} from "@page-containers/get-care/components/RelatedLinks";
import {PracticeApptReason} from "@services/types";
import {NextPage} from "next";
import {useTranslation} from "@i18n/client";
import React, {useMemo} from "react";
import Layout from "src/components/v5/Layout";
import {Care} from "src/utils/cms/types";

import {getOgImagePath} from "../../../opengraph/utils/encodeUrl";
import Faq, {QA, SectionDivider} from "../../components/v5/Faq/Faq";
import {NavTheme} from "../../components/v5/Nav/useNavState";
import ApptReasonFaq from "../../components/v5/Reason/ApptReasonFaq";
import {getCareDiscoveryColorScheme} from "../../constants/Reason/getCareDiscoveryColorScheme";
import {useTypedSelector} from "../../store";
import useMediaQuery from "../../useMediaQuery";
import {CareInsuranceType} from "../../utils/cms/types";
import {fetchLocationsByReasonId} from "../../utils/fetchLocationsByReasonId";
import {toTitleCase} from "../../utils/titleCase";
import {Wrapper} from "./components/Comp";

export type Props = {
  content: Care;
  reason: PracticeApptReason;
  apptReasonLocationIds: string[];
};

export const Reason: NextPage<Props> = ({content, reason, apptReasonLocationIds}) => {
  const i18n = useTranslation();
  const {locations, locationsSorted} = useTypedSelector(state => state.config);

  const locationsMatchingApptReason = useMemo(
    () => locations.filter(l => apptReasonLocationIds.includes(l.id)),
    [reason.id, locationsSorted],
  );

  const {backgroundColor, patternSrc} = getCareDiscoveryColorScheme(reason.id, content.name);

  const isSm = useMediaQuery("sm");

  const generalFaqs = useMemo(
    () =>
      content.shared_faqs?.reduce<(QA | SectionDivider)[]>(
        (acc, next) =>
          next.faqs ? [...acc, ...next.faqs.map(qa => ({q: qa.question, a: qa.answer}))] : acc,
        [],
      ) || [],
    [content.id],
  );

  const pageTitle = content.meta?.title || `${toTitleCase(content.name)} | Carbon Health`;

  return (
    <Layout
      key={JSON.stringify(isSm)} // Forces nav to render with isSm when not SSR
      pageTitle={pageTitle}
      pageDescription={content.meta?.description || content.description}
      navTheme={isSm ? NavTheme.BLACK : NavTheme.DEFAULT}
      navStartTransparent={isSm}
      wrapClasses="ovf-unset" // required for position sticky to work
      absolutePageImage={getOgImagePath("/get-care/[slug]", {id: reason.id, title: content.name})}
    >
      <Wrapper.Grid>
        <Wrapper.Faq backgroundColor={backgroundColor} patternSrc={patternSrc}>
          <ApptReasonFaq
            title={content.name}
            description={content.description}
            faq={content.faqs?.map(qa => ({q: qa.question, a: qa.answer})) || []}
            isInsuranceAccepted={content.insurance_type !== CareInsuranceType.NoInsurance}
          />
        </Wrapper.Faq>

        <Wrapper.Action isSticky={!isSm} hideGutters>
          <ClinicList locations={locationsMatchingApptReason} apptReason={reason} />
        </Wrapper.Action>

        <Wrapper.Insurances>
          <Insurances
            headerText={getCareInsuranceText(
              i18n.t.bind(i18n),
              content.insurance_type,
              content.name,
            )}
            insuranceType={content.insurance_type}
            isFullWidth={false}
          />
        </Wrapper.Insurances>

        <Wrapper.GlobalFaq>
          <Faq
            className="ph0-f ph0-md-f pt0-sm"
            faq={generalFaqs}
            supportClickEventData={{extraData: {apptReasonId: content.id}}}
            skipLinkTargetId="footer"
          />
        </Wrapper.GlobalFaq>
      </Wrapper.Grid>
      <RelatedLinks condition={content.name} relatedTo={content.related_to} />
    </Layout>
  );
};

export const getInitialPropsReason = async ({
  reasonContent,
  reason,
}: {
  reason: PracticeApptReason;
  reasonContent: Care;
}): Promise<Props> => {
  const apptReasonLocationIds = await fetchLocationsByReasonId(reason.id);

  return {
    apptReasonLocationIds,
    content: reasonContent,
    reason,
  };
};
